import React, { useState, useEffect } from 'react';
import { Sidebar } from './Dashboard';
import { API } from '../App';
import EmployeeCompare from './EmployeeCompare';
import BatchReportsTab, { generateDealerPDF, generateMultiSectionDealerPDF, loadLogo } from './BatchReports';
import { CODE_DESCRIPTIONS, CODE_ORDER } from '../codes';

function getSprColor(spr, maxSpr = 6) {
  const denom = Math.max((maxSpr || 6) - 1, 1);
  let ratio = (spr - 1) / denom;
  ratio = Math.max(0, Math.min(1, ratio));
  const r = Math.round(20 + ratio * 140);   // dark blue -> dark red
  const g = Math.round(60 - ratio * 45);
  const b = Math.round(160 - ratio * 130);
  return `rgb(${r},${g},${b})`;
}

// Highest SPR to expect for a game (drives the color scale). Roulette -> 9, BJ -> 6.
function maxSprForGame(game) {
  const forms = game?.form_config?.forms || [];
  let max = 6;
  forms.forEach(f => {
    const nums = (f.options || []).map(o => parseInt(o, 10)).filter(n => !isNaN(n));
    if (nums.length) max = Math.max(max, ...nums);
  });
  return max;
}

// Identify the full (non-EZ) and EZ variants within a game family.
function familyFullEz(family) {
  const games = family?.games || [];
  const ez = games.find(g => /ez/i.test(g.name));
  const full = games.find(g => !/ez/i.test(g.name)) || games[0];
  return { full, ez };
}

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#0f0f1a',
    display: 'flex',
  },
  main: {
    flex: 1,
    padding: '40px',
    overflowY: 'auto',
  },
  pageTitle: {
    color: '#ffffff',
    fontSize: '28px',
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  pageSubtitle: {
    color: '#a0a0b0',
    fontSize: '14px',
    marginBottom: '40px',
  },
  card: {
    backgroundColor: '#16213e',
    borderRadius: '16px',
    padding: '24px',
    marginBottom: '24px',
  },
  cardTitle: {
    color: '#ffffff',
    fontSize: '18px',
    fontWeight: 'bold',
    marginBottom: '20px',
  },
  filterRow: {
    display: 'flex',
    gap: '16px',
    alignItems: 'flex-start',
    marginBottom: '24px',
    flexWrap: 'wrap',
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    position: 'relative',
  },
  label: {
    color: '#a0a0b0',
    fontSize: '12px',
    letterSpacing: '1px',
    textTransform: 'uppercase',
  },
  input: {
    padding: '10px 14px',
    backgroundColor: '#0f3460',
    border: '1px solid #0f3460',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '14px',
    outline: 'none',
    minWidth: '220px',
  },
  searchButton: {
    padding: '10px 24px',
    backgroundColor: '#e94560',
    border: 'none',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
    alignSelf: 'flex-end',
  },
  dropdownList: {
    backgroundColor: '#0f3460',
    borderRadius: '8px',
    marginTop: '4px',
    maxHeight: '200px',
    overflowY: 'auto',
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    zIndex: 100,
  },
  dropdownItem: {
    padding: '10px 14px',
    color: '#ffffff',
    cursor: 'pointer',
    borderBottom: '1px solid #16213e',
    fontSize: '14px',
  },
  dropdownItemSelected: {
    padding: '10px 14px',
    color: '#e94560',
    cursor: 'pointer',
    borderBottom: '1px solid #16213e',
    fontSize: '14px',
    backgroundColor: '#16213e',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    color: '#a0a0b0',
    fontSize: '12px',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    padding: '12px 16px',
    textAlign: 'left',
    borderBottom: '1px solid #0f3460',
  },
  td: {
    color: '#ffffff',
    fontSize: '14px',
    padding: '14px 16px',
    borderBottom: '1px solid #0f3460',
  },
  viewButton: {
    padding: '6px 14px',
    backgroundColor: '#e94560',
    border: 'none',
    borderRadius: '6px',
    color: '#ffffff',
    fontSize: '12px',
    cursor: 'pointer',
  },
  // Row actions. One filled button carries the main action and the rest are
  // outlined, so four controls read as a set instead of four competing blocks.
  actionCell: {
    color: '#ffffff',
    fontSize: '14px',
    padding: '14px 16px',
    borderBottom: '1px solid #0f3460',
    whiteSpace: 'nowrap',
    textAlign: 'right',
  },
  actionRow: { display: 'inline-flex', gap: '6px', alignItems: 'center' },
  rowBtn: {
    padding: '6px 11px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    borderWidth: '1px',
    borderStyle: 'solid',
    lineHeight: 1.4,
  },
  rowBtnPrimary: { backgroundColor: '#e94560', borderColor: '#e94560', color: '#ffffff' },
  rowBtnGhost: { backgroundColor: 'transparent', borderColor: '#2a4a7a', color: '#a8b8d8' },
  rowBtnWarn: { backgroundColor: 'transparent', borderColor: '#8a6d1f', color: '#d4af37' },
  rowBtnDanger: { backgroundColor: 'transparent', borderColor: '#7a2f2f', color: '#e07a7a' },
  // Game filter: each family is one unit - the family name selects the whole
  // set, the chips beside it pick a single variant.
  gameGroup: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px',
    borderRadius: '10px',
    border: '1px solid #0f3460',
    backgroundColor: '#12203f',
  },
  gameAll: {
    padding: '6px 13px',
    borderRadius: '7px',
    fontSize: '13px',
    fontWeight: 'bold',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    border: '1px solid transparent',
    backgroundColor: 'transparent',
    color: '#8a9bbd',
  },
  gameAllOn: {
    padding: '6px 13px',
    borderRadius: '7px',
    fontSize: '13px',
    fontWeight: 'bold',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    border: '1px solid #e94560',
    backgroundColor: '#e94560',
    color: '#ffffff',
  },
  gameChip: {
    padding: '5px 11px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    border: '1px solid #2a4a7a',
    backgroundColor: 'transparent',
    color: '#8a9bbd',
  },
  gameChipOn: {
    padding: '5px 11px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    border: '1px solid #4a7ac0',
    backgroundColor: '#1d3a6b',
    color: '#ffffff',
  },
  purposeBadge: {
    display: 'inline-block',
    backgroundColor: '#e94560',
    color: '#ffffff',
    fontSize: '10px',
    fontWeight: 'bold',
    padding: '2px 7px',
    borderRadius: '4px',
    letterSpacing: '0.5px',
  },
  select: {
    padding: '10px 14px',
    backgroundColor: '#0f3460',
    border: '1px solid #0f3460',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '14px',
    outline: 'none',
  },
  tabRow: {
    display: 'flex',
    gap: '8px',
    marginBottom: '32px',
  },
  tab: {
    padding: '10px 20px',
    backgroundColor: '#0f3460',
    border: '2px solid #0f3460',
    borderRadius: '8px',
    color: '#a0a0b0',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  tabActive: {
    padding: '10px 20px',
    backgroundColor: '#0f3460',
    border: '2px solid #e94560',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  gameTab: {
    padding: '8px 16px',
    backgroundColor: '#0f3460',
    border: '2px solid #0f3460',
    borderRadius: '8px',
    color: '#a0a0b0',
    fontSize: '13px',
    fontWeight: 'bold',
    cursor: 'pointer',
    marginLeft: '8px',
  },
  gameTabActive: {
    padding: '8px 16px',
    backgroundColor: '#e94560',
    border: '2px solid #e94560',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '13px',
    fontWeight: 'bold',
    cursor: 'pointer',
    marginLeft: '8px',
  },
  codeRow: {
    marginBottom: '16px',
  },
  codeHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '6px',
  },
  codeName: {
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: 'bold',
  },
  codeValue: {
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: 'bold',
  },
  codeDetails: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '6px',
  },
  benchmark: {
    color: '#a0a0b0',
    fontSize: '12px',
  },
  vsGood: {
    color: '#00cc00',
    fontSize: '12px',
    fontWeight: 'bold',
  },
  vsBad: {
    color: '#e94560',
    fontSize: '12px',
    fontWeight: 'bold',
  },
  barContainer: {
    height: '6px',
    backgroundColor: '#0f3460',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  barGood: {
    height: '6px',
    backgroundColor: '#00cc00',
    borderRadius: '4px',
  },
  barBad: {
    height: '6px',
    backgroundColor: '#e94560',
    borderRadius: '4px',
  },
  dealerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  dealerName: {
    color: '#ffffff',
    fontSize: '22px',
    fontWeight: 'bold',
  },
  backButton: {
    padding: '8px 20px',
    backgroundColor: 'transparent',
    border: '1px solid #0f3460',
    borderRadius: '8px',
    color: '#a0a0b0',
    fontSize: '14px',
    cursor: 'pointer',
  },
  noData: {
    color: '#a0a0b0',
    textAlign: 'center',
    padding: '40px',
    fontSize: '16px',
  },
};

function Reports() {
  const token = localStorage.getItem('admin_token');
  const [view, setView] = useState('sessions');
  const [dealers, setDealers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedDealer, setSelectedDealer] = useState('');
  const [dealerSearch, setDealerSearch] = useState('');
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionReport, setSessionReport] = useState(null);
  const [dealerHistory, setDealerHistory] = useState([]);
  const [dealerDemPools, setDealerDemPools] = useState({});
  const [dealerSurvey, setDealerSurvey] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [shiftFilter, setShiftFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [showExcluded, setShowExcluded] = useState(false);
  const [purposeFilter, setPurposeFilter] = useState('');
  const [gameTypes, setGameTypes] = useState([]);
  const [selectedGameIds, setSelectedGameIds] = useState(null); // null = unset (loaded once gameTypes arrive)
  const isAdmin = localStorage.getItem('admin_role') === 'admin';

  // Group games by family. families = [{id, name, games: [...]}, ...] including a synthetic "Unfamilied" bucket.
  const families = (() => {
    const map = new Map();
    gameTypes.forEach(g => {
      // Custom games are separate experiments: each gets its own section.
      const inFamily = g.family_id && !g.is_custom;
      const key = inFamily ? g.family_id : `__solo__${g.id}`;
      const name = inFamily ? g.family_name : g.name;
      if (!map.has(key)) map.set(key, { id: key, name, family_id: inFamily ? g.family_id : null, games: [] });
      map.get(key).games.push(g);
    });
    return Array.from(map.values());
  })();
  const allGamesSelected = gameTypes.length > 0 &&
    gameTypes.every(g => (selectedGameIds || []).includes(g.id));

  // Inside a family the family name is already on the group, so the chip only
  // needs what makes this variant different: "Blackjack EZ" -> "EZ".
  function variantLabel(family, game) {
    if (!game.name.startsWith(family.name)) return game.name;
    return game.name.slice(family.name.length).trim() || 'Full';
  }

  useEffect(() => {
    loadDealers();
    loadGameTypes();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Once game types are loaded, default the session-list filter to "all selected".
  useEffect(() => {
    if (selectedGameIds === null && gameTypes.length > 0) {
      setSelectedGameIds(gameTypes.map(g => g.id));
    }
  }, [gameTypes]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const t = setTimeout(() => loadSessions(shiftFilter, searchFilter, selectedGameIds), 300);
    return () => clearTimeout(t);
  }, [shiftFilter, searchFilter, selectedGameIds]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadDealers() {
    const res = await fetch(`${API}/admin/dealers`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const data = await res.json();
    if (Array.isArray(data)) setDealers(data);
  }

  async function loadGameTypes() {
    const res = await fetch(`${API}/game-types`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const data = await res.json();
    if (Array.isArray(data)) setGameTypes(data);
  }

  async function loadSessions(shift = '', search = '', gameIds = null) {
    const params = new URLSearchParams();
    if (shift) params.append('shift', shift);
    if (search) {
      params.append('search', search);
      params.append('limit', '500');
    }
    // Only send the filter if it's an active narrowing — empty array = no rows; full array = no filter needed.
    if (Array.isArray(gameIds) && gameIds.length > 0 && gameIds.length < gameTypes.length) {
      params.append('game_type_ids', gameIds.join(','));
    } else if (Array.isArray(gameIds) && gameIds.length === 0) {
      // explicit "no games selected" — short-circuit to empty list, don't bother the backend
      setSessions([]);
      return;
    }
    const res = await fetch(`${API}/admin/sessions?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const data = await res.json();
    if (Array.isArray(data)) setSessions(data);
  }

  async function excludeSession(sessionId, excluded) {
    const reason = excluded ? prompt('Optional reason for excluding this session:') : null;
    if (excluded && reason === null) return;
    try {
      await fetch(`${API}/admin/sessions/${sessionId}/exclude`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ excluded, reason }),
      });
      loadSessions(shiftFilter);
    } catch (e) {
      alert('Error updating session');
    }
  }

  async function changePurpose(session) {
    const next = session.data_purpose === 'benchmark' ? 'standard' : 'benchmark';
    const label = next === 'benchmark' ? 'Benchmark' : 'Standard';
    const reason = prompt(`Reason for changing this test to ${label} (required):`);
    if (reason === null) return;
    if (!reason.trim()) { alert('A reason is required.'); return; }
    try {
      const res = await fetch(`${API}/admin/sessions/${session.id}/purpose`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ purpose: next, reason }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        alert(typeof data.detail === 'string' ? data.detail : 'Error updating session');
        return;
      }
      loadSessions(shiftFilter);
    } catch (e) {
      alert('Error updating session');
    }
  }

  async function deleteSession(sessionId, dealerName) {
    const confirmed = window.confirm(
      `Permanently delete this session for ${dealerName}?\n\nThis will remove ALL rounds, time codes and stats for this session. This CANNOT be undone.`
    );
    if (!confirmed) return;
    const doubleConfirm = window.confirm(
      'Are you absolutely sure? This data will be permanently deleted.'
    );
    if (!doubleConfirm) return;
    try {
      await fetch(`${API}/admin/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      loadSessions(shiftFilter);
    } catch (e) {
      alert('Error deleting session');
    }
  }

  async function viewSession(session) {
    setSelectedSession(session);
    setView('session-detail');
    setSessionReport(null);
    setSessionDem(null);
    setRoundsBreakdown(null);
    setExpandedRounds({});
    const auth = { headers: { Authorization: `Bearer ${token}` } };
    const [report, dem, breakdown] = await Promise.all([
      fetch(`${API}/sessions/${session.id}/report`, auth).then(r => r.json()).catch(() => null),
      fetch(`${API}/sessions/${session.id}/dem`, auth).then(r => r.json()).catch(() => null),
      fetch(`${API}/sessions/${session.id}/rounds-breakdown`, auth).then(r => r.json()).catch(() => null),
    ]);
    setSessionReport(report);
    setSessionDem(dem);
    setRoundsBreakdown(breakdown);
  }

  async function viewDealerHistory() {
    if (!selectedDealer) return;
    setLoading(true);
    setVariantByFamily({});
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    const res = await fetch(`${API}/admin/dealers/${selectedDealer}/history?${params}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const data = await res.json();
    setDealerHistory(data.history || []);
    setDealerDemPools(data.dem_pools || {});
    const minRounds = data.dem_min_rounds || 20;
    const standing = data.dem_standing || {};
    setDemCache(Object.fromEntries(gameTypes.map(g =>
      [g.id, standing[g.id] || { not_ranked: true, min_rounds: minRounds }])));
    setDealerSurvey([]);
    fetch(`${API}/admin/dealers/${selectedDealer}/survey-history?${params}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(d => setDealerSurvey(Array.isArray(d.games) ? d.games : []))
      .catch(() => {});
    setView('dealer-history');
    setLoading(false);
  }

  function toggleGameFilter(gameId) {
    setSelectedGameIds(prev => {
      const list = prev || [];
      return list.includes(gameId) ? list.filter(id => id !== gameId) : [...list, gameId];
    });
  }

  function toggleFamilyFilter(family) {
    const familyIds = family.games.map(g => g.id);
    setSelectedGameIds(prev => {
      const list = prev || [];
      const allIn = familyIds.every(id => list.includes(id));
      if (allIn) return list.filter(id => !familyIds.includes(id));
      return Array.from(new Set([...list, ...familyIds]));
    });
  }

  const filteredDealers = dealers.filter(d =>
    dealerSearch &&
    (
      d.payroll_number.toLowerCase().includes(dealerSearch.toLowerCase()) ||
      d.first_name.toLowerCase().includes(dealerSearch.toLowerCase()) ||
      d.last_name.toLowerCase().includes(dealerSearch.toLowerCase())
    )
  );

  const [expandedCodes, setExpandedCodes] = React.useState({});
  const [sessionDem, setSessionDem] = React.useState(null);
  const [roundsBreakdown, setRoundsBreakdown] = React.useState(null);
  const [expandedRounds, setExpandedRounds] = React.useState({});
  const [demCache, setDemCache] = React.useState({});
  const [variantByFamily, setVariantByFamily] = React.useState({});

  function renderSessionDetail() {
    if (!sessionReport) return <div style={styles.noData}>Loading...</div>;
    const results = sessionReport.results || [];
    const ordered = CODE_ORDER.map(c => results.find(r => r.code === c)).filter(Boolean);
    const shuffle = ordered.filter(r => ['TST','AST'].includes(r.code));
    const change = ordered.filter(r => ['TCT','ACT','OTT','AOT'].includes(r.code));
    // Everything else belongs to the round itself, whatever the game calls it.
    const dealing = ordered.filter(r => !shuffle.includes(r) && !change.includes(r));

    function renderCode(result) {
      const hasBench = result.vs_benchmark !== null && result.vs_benchmark !== undefined;
      const isGood = hasBench && result.vs_benchmark <= 0;
      const width = Math.min(Math.round((result.avg_seconds / (result.benchmark || 1)) * 100), 100);
      const hasBreakdown = result.variable_breakdown && result.variable_breakdown.length > 0;
      const isExpanded = expandedCodes[result.code];

      return (
        <div key={result.code} style={styles.codeRow}>
          <div style={styles.codeHeader}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={styles.codeName}>{result.code} — {CODE_DESCRIPTIONS[result.code]}</span>
              {hasBreakdown && (
                <button
                  style={{
                    padding: '2px 8px',
                    backgroundColor: 'transparent',
                    border: '1px solid #0f3460',
                    borderRadius: '4px',
                    color: '#a0a0b0',
                    fontSize: '11px',
                    cursor: 'pointer',
                  }}
                  onClick={() => setExpandedCodes(prev => ({ ...prev, [result.code]: !prev[result.code] }))}
                >
                  {isExpanded ? '▲ Hide' : `▼ ${result.variable_breakdown.length} combos`}
                </button>
              )}
            </div>
            <span style={styles.codeValue}>{result.avg_seconds}s</span>
          </div>
          <div style={styles.codeDetails}>
            <span style={styles.benchmark}>
              Benchmark: {hasBench ? `${result.benchmark}s` : 'not set'} · {result.count} rounds
            </span>
            {hasBench && (
              <span style={isGood ? styles.vsGood : styles.vsBad}>
                {isGood ? '▼' : '▲'} {Math.abs(result.vs_benchmark)}s {isGood ? 'under' : 'over'}
              </span>
            )}
          </div>
          <div style={styles.barContainer}>
            <div style={{ ...(isGood ? styles.barGood : styles.barBad), width: `${width}%` }} />
          </div>

          {isExpanded && hasBreakdown && (
            <div style={{ marginTop: '12px', borderLeft: '2px solid #0f3460', paddingLeft: '16px' }}>
              {result.variable_breakdown.map((v, i) => {
                const vGood = v.vs_benchmark !== null && v.vs_benchmark <= 0;
                const vWidth = Math.min(Math.round((v.avg_seconds / (v.benchmark || 1)) * 100), 100);
                return (
                  <div key={i} style={{ marginBottom: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <span style={{ color: '#a0a0b0', fontSize: '12px' }}>{v.label}</span>
                      <span style={{ color: '#ffffff', fontSize: '12px', fontWeight: 'bold' }}>{v.avg_seconds}s</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <span style={{ color: '#a0a0b0', fontSize: '11px' }}>
                        Benchmark: {v.benchmark ? `${v.benchmark}s` : 'not set'} · {v.count} rounds
                      </span>
                      {v.vs_benchmark !== null && (
                        <span style={{ color: vGood ? '#00cc00' : '#e94560', fontSize: '11px', fontWeight: 'bold' }}>
                          {vGood ? '▼' : '▲'} {Math.abs(v.vs_benchmark)}s {vGood ? 'under' : 'over'}
                        </span>
                      )}
                    </div>
                    <div style={{ height: '4px', backgroundColor: '#0f3460', borderRadius: '2px', overflow: 'hidden' }}>
                      <div style={{
                        height: '4px',
                        backgroundColor: vGood ? '#00cc00' : '#e94560',
                        borderRadius: '2px',
                        width: `${vWidth}%`,
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
      }

    // difference = benchmark - actual; positive = faster than benchmark (good).
    function fmtDiff(d) {
      if (d == null) return <span style={{ color: '#a0a0b0' }}>—</span>;
      const good = d >= 0;
      return (
        <span style={{ color: good ? '#00cc00' : '#e94560', fontWeight: 'bold' }}>
          {good ? '+' : ''}{d}s
        </span>
      );
    }

    function renderRoundRow(rd) {
      const open = expandedRounds[rd.round_number];
      const isDeal = rd.round_type === 'deal';
      return (
        <div key={rd.round_number} style={{ borderBottom: '1px solid #0f3460' }}>
          <div
            style={{ display: 'flex', alignItems: 'center', padding: '10px 8px', cursor: rd.processes.length ? 'pointer' : 'default' }}
            onClick={() => rd.processes.length && setExpandedRounds(prev => ({ ...prev, [rd.round_number]: !prev[rd.round_number] }))}
          >
            <span style={{ flex: '0 0 70px', color: '#ffffff', fontWeight: 'bold' }}>
              #{rd.round_number}
              {!isDeal && <span style={{ color: '#a0a0b0', fontWeight: 'normal', fontSize: '11px', textTransform: 'capitalize' }}> {rd.round_type}</span>}
            </span>
            <span style={{ flex: '0 0 50px', textAlign: 'center', color: '#a0a0b0' }}>{isDeal ? (rd.spr || '—') : '—'}</span>
            <span style={{ flex: 1, textAlign: 'right', color: '#ffffff' }}>{rd.total_actual}s</span>
            <span style={{ flex: 1, textAlign: 'right', color: '#a0a0b0' }}>{rd.total_benchmark != null ? `${rd.total_benchmark}s` : '—'}</span>
            <span style={{ flex: 1, textAlign: 'right' }}>{fmtDiff(rd.difference)}</span>
            <span style={{ flex: '0 0 24px', textAlign: 'right', color: '#a0a0b0', fontSize: '11px' }}>{rd.processes.length ? (open ? '▲' : '▼') : ''}</span>
          </div>
          {open && rd.processes.length > 0 && (
            <div style={{ padding: '4px 8px 12px 24px', borderLeft: '2px solid #0f3460', margin: '0 8px 8px' }}>
              {rd.processes.map((p, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '4px 0', fontSize: '13px' }}>
                  <span style={{ flex: 1, color: '#a0a0b0' }}>{p.code}{CODE_DESCRIPTIONS[p.code] ? ` — ${CODE_DESCRIPTIONS[p.code]}` : ''}</span>
                  <span style={{ flex: '0 0 70px', textAlign: 'right', color: '#ffffff' }}>{p.actual}s</span>
                  <span style={{ flex: '0 0 90px', textAlign: 'right', color: '#a0a0b0' }}>{p.benchmark != null ? `${p.benchmark}s` : '—'}</span>
                  <span style={{ flex: '0 0 70px', textAlign: 'right' }}>{fmtDiff(p.difference)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <div>
        <div style={styles.dealerHeader}>
          <div>
            <div style={styles.dealerName}>{selectedSession?.dealer_name}</div>
            <div style={{ color: '#a0a0b0', fontSize: '14px' }}>
              {[
                new Date(selectedSession?.started_at).toLocaleDateString(),
                selectedSession?.pit,
                selectedSession?.table_number || 'No table',
                selectedSession?.table_min,
                selectedSession?.shift,
                selectedSession?.test_type,
                selectedSession?.shuffle_type,
                selectedSession?.deck_count,
              ].filter(Boolean).join(' · ')}
            </div>
            <div style={{ color: '#a0a0b0', fontSize: '13px', marginTop: '4px' }}>
              Measured against: <b style={{ color: '#ffffff' }}>
                {sessionDem?.benchmark_label || 'Provisional — no benchmark set'}
              </b>
            </div>
          </div>
          <button style={styles.backButton} onClick={() => setView('sessions')}>← Back</button>
        </div>

        {sessionReport?.stats && (
          <div style={styles.card}>
            <div style={styles.cardTitle}>Primary Stats</div>
            <div style={{ display: 'flex', justifyContent: 'space-around', textAlign: 'center' }}>
              <div>
                <div style={{ color: '#00cc00', fontSize: '36px', fontWeight: 'bold' }}>
                  {sessionDem && sessionDem.session_dem != null ? sessionDem.session_dem.toFixed(2) : '—'}
                </div>
                <div style={{ color: '#a0a0b0', fontSize: '12px', marginTop: '4px' }}>DEM</div>
              </div>
              <div>
                <div style={{ color: '#00cc00', fontSize: '36px', fontWeight: 'bold' }}>
                  {sessionDem && sessionDem.dealer_percentile != null ? `${sessionDem.dealer_percentile}%` : '—'}
                </div>
                <div style={{ color: '#a0a0b0', fontSize: '12px', marginTop: '4px' }}>Percentile</div>
              </div>
              <div>
                <div style={{ color: '#e94560', fontSize: '30px', fontWeight: 'bold' }}>
                  {sessionReport.stats.rph || '—'} <span style={{ fontSize: '18px', color: '#a0a0b0' }}>@</span> {sessionReport.stats.spr || '—'}
                </div>
                <div style={{ color: '#a0a0b0', fontSize: '12px', marginTop: '4px' }}>RPH @ SPR</div>
              </div>
              <div>
                <div style={{ color: '#e94560', fontSize: '36px', fontWeight: 'bold' }}>
                  {sessionReport.stats.hph || '—'}
                </div>
                <div style={{ color: '#a0a0b0', fontSize: '12px', marginTop: '4px' }}>HPH</div>
              </div>
            </div>
            <div style={{ color: '#a0a0b0', fontSize: '12px', textAlign: 'center', marginTop: '14px', lineHeight: 1.6 }}>
              {sessionReport.stats.round_count || 0} rounds this session
              {sessionDem && sessionDem.session_dem == null &&
                ` · session DEM needs ≥${sessionDem.session_min_rounds} rounds`}
              <br />
              {sessionDem && sessionDem.dealer_dem != null ? (
                <>Dealer overall: <b style={{ color: '#ffffff' }}>{sessionDem.dealer_dem.toFixed(2)} DEM</b>
                  {sessionDem.dealer_percentile != null && ` · ${sessionDem.dealer_percentile}th percentile`}
                  {` · ${sessionDem.dealer_rounds} rounds across all sessions`}</>
              ) : (
                <>Dealer not yet ranked · needs ≥{sessionDem?.pool_min_rounds || 20} rounds in this game to earn a DEM &amp; percentile</>
              )}
            </div>
          </div>
        )}

        {roundsBreakdown?.rounds?.length > 0 && (
          <div style={styles.card}>
            <div style={styles.cardTitle}>Round-by-Round (Benchmark − Time = Difference)</div>
            <div style={{ display: 'flex', color: '#a0a0b0', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', padding: '0 8px 8px', borderBottom: '1px solid #0f3460' }}>
              <span style={{ flex: '0 0 70px' }}>Round</span>
              <span style={{ flex: '0 0 50px', textAlign: 'center' }}>SPR</span>
              <span style={{ flex: 1, textAlign: 'right' }}>Time</span>
              <span style={{ flex: 1, textAlign: 'right' }}>Benchmark</span>
              <span style={{ flex: 1, textAlign: 'right' }}>Difference</span>
              <span style={{ flex: '0 0 24px' }} />
            </div>
            {roundsBreakdown.rounds.map(rd => renderRoundRow(rd))}
          </div>
        )}
        {dealing.length > 0 && (
          <div style={styles.card}>
            <div style={styles.cardTitle}>Dealing Round</div>
            {dealing.map(renderCode)}
          </div>
        )}
        {shuffle.length > 0 && (
          <div style={styles.card}>
            <div style={styles.cardTitle}>Shuffle</div>
            {shuffle.map(renderCode)}
          </div>
        )}
        {change.length > 0 && (
          <div style={styles.card}>
            <div style={styles.cardTitle}>Change</div>
            {change.map(renderCode)}
          </div>
        )}
      </div>
    );
  }

  function renderDealerHistory() {
    const dealer = dealers.find(d => d.id === selectedDealer);
    if (!dealerHistory.length) return (
      <div>
        <div style={styles.dealerHeader}>
          <div style={styles.dealerName}>{dealer?.first_name} {dealer?.last_name}</div>
          <button style={styles.backButton} onClick={() => setView('dealer-history-search')}>← Back</button>
        </div>
        <div style={styles.noData}>No session data found for this dealer</div>
      </div>
    );

    async function printDealerReport() {
      const tenantName = localStorage.getItem('admin_name') || 'Casino';
      const logo = await loadLogo();
      // One section (own chart + stats) per game type, so games aren't mixed.
      const byGame = {};
      dealerHistory.forEach(r => {
        (byGame[r.game_type_id] = byGame[r.game_type_id] || []).push(r);
      });
      const sections = gameTypes
        .filter(g => (byGame[g.id] || []).length)
        .map(g => ({ label: g.name, history: byGame[g.id], demPools: dealerDemPools }));
      const pdf = sections.length
        ? generateMultiSectionDealerPDF(dealer, sections, tenantName, logo, dealerSurvey)
        : generateDealerPDF(dealer, dealerHistory, tenantName, logo, { demPools: dealerDemPools });
      pdf.save(`${dealer.payroll_number}_${dealer.last_name}_${dealer.first_name}.pdf`);
    }

    const gameNameById = {};
    gameTypes.forEach(g => { gameNameById[g.id] = g.name; });

    function renderHphChart(rows, maxSpr) {
      if (!rows.length) {
        return <div style={{ color: '#a0a0b0', fontSize: '13px', padding: '20px', textAlign: 'center' }}>No sessions for this version yet.</div>;
      }
      // Fixed visible width; once the tests no longer fit at a readable spacing the
      // chart grows and scrolls sideways (opening at the most recent tests).
      const padding = { top: 24, right: 40, bottom: 40, left: 60 };
      const CHART_WIDTH = 900;
      const MIN_POINT_SPACING = 60;
      const width = Math.max(CHART_WIDTH, padding.left + padding.right + (rows.length - 1) * MIN_POINT_SPACING);
      const height = 300;
      const chartW = width - padding.left - padding.right;
      const chartH = height - padding.top - padding.bottom;
      const vals = [];
      rows.forEach(d => { if (d.hph) vals.push(d.hph); if (d.benchmark_hph) vals.push(d.benchmark_hph); });
      const minY = vals.length ? Math.min(...vals) * 0.9 : 0;
      const maxY = vals.length ? Math.max(...vals) * 1.1 : 100;
      const xScale = i => (rows.length === 1
        ? padding.left + chartW / 2
        : padding.left + (i / (rows.length - 1)) * chartW);
      const yScale = v => padding.top + chartH - ((v - minY) / ((maxY - minY) || 1)) * chartH;
      const yTicks = 5;
      const yTickValues = Array.from({ length: yTicks }, (_, i) => Math.round(minY + (i / (yTicks - 1)) * (maxY - minY)));
      return (
        <div
          style={{ overflowX: 'auto', width: CHART_WIDTH, maxWidth: '100%', borderRadius: 8 }}
          ref={el => { if (el && !el.dataset.scrolled) { el.scrollLeft = el.scrollWidth; el.dataset.scrolled = '1'; } }}
        >
          <svg width={width} height={height} style={{ display: 'block' }}>
            <rect x={0} y={0} width={width} height={height} fill="#d6e8f7" rx={8} />
            {yTickValues.map(v => (
              <g key={v}>
                <line x1={padding.left} x2={width - padding.right} y1={yScale(v)} y2={yScale(v)} stroke="#a8c6e0" strokeWidth={1} />
                <text x={padding.left - 8} y={yScale(v)} fill="#1f2430" fontSize={11} textAnchor="end" dominantBaseline="central">{v}</text>
              </g>
            ))}
            {rows.map((d, i) => (
              <text key={'x' + i} x={xScale(i)} y={height - 10} fill="#1f2430" fontSize={10} textAnchor="middle">
                {new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </text>
            ))}
            {/* Dots first, benchmark markers after (so the + sits on top of the dots). */}
            {rows.map((d, i) => {
              if (!d.hph) return null;
              const cx = xScale(i), cy = yScale(d.hph);
              const color = d.spr ? getSprColor(d.spr, maxSpr) : '#444';
              const gname = gameNameById[d.game_type_id] || '';
              const vs = d.vs_benchmark != null ? ` (${d.vs_benchmark >= 0 ? '+' : ''}${d.vs_benchmark} vs bench)` : '';
              const tip = `${new Date(d.date).toLocaleDateString()}${gname ? ' · ' + gname : ''}\n`
                + `HPH ${d.hph}${d.benchmark_hph ? ` · benchmark ${d.benchmark_hph}${vs}` : ''}\n`
                + `RPH ${d.rph != null ? d.rph : '—'} @ SPR ${d.spr != null ? d.spr : '—'}`
                + `${d.rounds != null ? `\n${d.rounds} rounds` : ''}`;
              return (
                <g key={'dot' + i} style={{ cursor: 'pointer' }}>
                  <title>{tip}</title>
                  <text x={cx} y={cy - 14} fill="#111820" fontSize={11} fontWeight="bold" textAnchor="middle">{d.hph}</text>
                  <circle cx={cx} cy={cy} r={8} fill={color} stroke="#ffffff" strokeWidth={2} />
                </g>
              );
            })}
            {rows.map((d, i) => {
              if (!d.hph || !d.benchmark_hph) return null;
              const cx = xScale(i), by = yScale(d.benchmark_hph);
              const mColor = d.vs_benchmark > 0 ? '#00cc00' : '#ffcc00';
              return (
                <g key={'bm' + i}>
                  <line x1={cx - 9} x2={cx + 9} y1={by} y2={by} stroke={mColor} strokeWidth={3} strokeLinecap="round" />
                  <line x1={cx} x2={cx} y1={by - 7} y2={by + 7} stroke={mColor} strokeWidth={3} strokeLinecap="round" />
                </g>
              );
            })}
          </svg>
        </div>
      );
    }

    function renderFamilySection(family) {
      const { full, ez } = familyFullEz(family);
      const base = full || ez;
      if (!base) return null;
      const hasEz = !!full && !!ez;
      const variant = variantByFamily[family.id] || 'all';
      const familyGameIds = family.games.map(g => String(g.id));
      let rows, maxSpr, demGames;
      if (variant === 'full') {
        rows = dealerHistory.filter(r => String(r.game_type_id) === String(full.id));
        maxSpr = maxSprForGame(full);
        demGames = [full];
      } else if (variant === 'ez' && ez) {
        rows = dealerHistory.filter(r => String(r.game_type_id) === String(ez.id));
        maxSpr = maxSprForGame(ez);
        demGames = [ez];
      } else { // 'all' (default)
        rows = dealerHistory.filter(r => familyGameIds.includes(String(r.game_type_id)));
        maxSpr = Math.max(...family.games.map(maxSprForGame));
        demGames = family.games;
      }
      rows = rows.slice().sort((a, b) => new Date(a.date) - new Date(b.date));
      return (
        <div key={family.id} style={styles.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
            <div style={styles.cardTitle}>{family.name} — HPH Over Time</div>
            {hasEz && (
              <div>
                <button style={variant === 'all' ? styles.gameTabActive : styles.gameTab}
                  onClick={() => setVariantByFamily(p => ({ ...p, [family.id]: 'all' }))}>{family.name}</button>
                <button style={variant === 'full' ? styles.gameTabActive : styles.gameTab}
                  onClick={() => setVariantByFamily(p => ({ ...p, [family.id]: 'full' }))}>{full.name}</button>
                <button style={variant === 'ez' ? styles.gameTabActive : styles.gameTab}
                  onClick={() => setVariantByFamily(p => ({ ...p, [family.id]: 'ez' }))}>{ez.name}</button>
              </div>
            )}
          </div>
          <div style={{ color: '#a0a0b0', fontSize: '13px', marginBottom: '8px' }}>
            {demGames.map(g => {
              const dem = demCache[g.id];
              if (!dem) return null;
              if (dem.not_ranked) {
                return <span key={g.id} style={{ marginRight: '16px' }}>{g.name}: not ranked (needs ≥{dem.min_rounds || 20})</span>;
              }
              return (
                <span key={g.id} style={{ marginRight: '16px' }}>
                  <b style={{ color: '#00cc00' }}>{g.name} {dem.dem != null ? dem.dem.toFixed(2) : '—'}</b>
                  {dem.percentile != null ? ` · ${dem.percentile}th pct` : ''} · {dem.rounds} rds
                </span>
              );
            })}
          </div>
          <div style={{ color: '#a0a0b0', fontSize: '12px', marginBottom: '12px' }}>
            Dot color = SPR (dark blue = light load → dark red = heavy). Benchmark marker: bright green = above, bright yellow = under. Hover a point for details.
          </div>
          {renderHphChart(rows, maxSpr)}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '16px', alignItems: 'center' }}>
            {Array.from({ length: maxSpr }, (_, k) => k + 1).map(spr => (
              <div key={spr} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: getSprColor(spr, maxSpr) }} />
                <span style={{ color: '#a0a0b0', fontSize: '12px' }}>SPR {spr}</span>
              </div>
            ))}
            <span style={{ color: '#00cc00', fontSize: '16px', fontWeight: 'bold' }}>✚ <span style={{ color: '#a0a0b0', fontSize: '12px', fontWeight: 'normal' }}>above</span></span>
            <span style={{ color: '#ffcc00', fontSize: '16px', fontWeight: 'bold' }}>✚ <span style={{ color: '#a0a0b0', fontSize: '12px', fontWeight: 'normal' }}>under</span></span>
          </div>
        </div>
      );
    }

    // Tests keep the benchmark that was in force when they were dealt, so a history
    // spanning a change of benchmark names each one.
    const benchmarksUsed = [...new Set(dealerHistory.map(r => r.benchmark_label).filter(Boolean))];

    return (
      <div>
        <div style={styles.dealerHeader}>
          <div>
            <div style={styles.dealerName}>{dealer?.first_name} {dealer?.last_name}</div>
            <div style={{ color: '#a0a0b0', fontSize: '13px', marginTop: '4px' }}>
              Measured against: <b style={{ color: '#ffffff' }}>
                {benchmarksUsed.length ? benchmarksUsed.join(', ') : 'Provisional — no benchmark set'}
              </b>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              style={{ ...styles.viewButton, padding: '8px 18px', fontSize: '13px' }}
              onClick={printDealerReport}
            >
              Print Report
            </button>
            <button style={styles.backButton} onClick={() => setView('dealer-history-search')}>← Back</button>
          </div>
        </div>
        {families.map(fam => renderFamilySection(fam))}
        {dealerSurvey.length > 0 && (
          <div style={styles.card}>
            <div style={styles.cardTitle}>Dealer Survey History</div>
            <div style={{ color: '#a0a0b0', fontSize: '13px', marginBottom: '12px' }}>
              Scores from your custom survey questions. Kept separate from timing and benchmark data.
            </div>
            {dealerSurvey.map(g => (
              <div key={g.game_type_id} style={{ marginBottom: '20px' }}>
                <div style={{ color: '#ffffff', fontSize: '15px', fontWeight: 'bold', margin: '8px 0' }}>{g.game_name}</div>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Date</th>
                      {g.questions.map(q => (
                        <th key={q.id} style={{ ...styles.th, textAlign: 'center', maxWidth: '180px' }} title={q.question_text}>
                          Survey {q.slot}
                          <div style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 'normal', fontSize: '11px', marginTop: '4px' }}>
                            {q.question_text} (1–{q.scale_max})
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {g.rows.map(r => (
                      <tr key={r.session_id}>
                        <td style={styles.td}>{new Date(r.date).toLocaleDateString()}</td>
                        {g.questions.map(q => (
                          <td key={q.id} style={{ ...styles.td, textAlign: 'center' }}>{r.scores[q.id] ?? '—'}</td>
                        ))}
                      </tr>
                    ))}
                    <tr>
                      <td style={{ ...styles.td, fontWeight: 'bold', color: '#e94560' }}>Average</td>
                      {g.questions.map(q => (
                        <td key={q.id} style={{ ...styles.td, textAlign: 'center', fontWeight: 'bold', color: '#e94560' }}>
                          {g.averages[q.id] != null ? g.averages[q.id].toFixed(1) : '—'}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
        <div style={styles.card}>
          <div style={styles.cardTitle}>Session History</div>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Game</th>
                <th style={styles.th}>Purpose</th>
                <th style={styles.th}>Benchmark</th>
                <th style={styles.th}>Rounds</th>
                <th style={styles.th}>RPH @ SPR</th>
                <th style={styles.th}>HPH</th>
                <th style={styles.th}>TCO</th>
                <th style={styles.th}>TTD</th>
                <th style={styles.th}>TPU</th>
                <th style={styles.th}>DPT</th>
                <th style={styles.th}>TRT</th>
              </tr>
            </thead>
            <tbody>
              {dealerHistory.map((s, i) => (
                <tr key={i}>
                  <td style={styles.td}>{new Date(s.date).toLocaleDateString()}</td>
                  <td style={styles.td}>{gameNameById[s.game_type_id] || '—'}</td>
                  <td style={{ ...styles.td, color: s.data_purpose === 'benchmark' ? '#e94560' : '#a0a0b0', fontWeight: s.data_purpose === 'benchmark' ? 'bold' : 'normal' }}>
                    {s.data_purpose === 'benchmark' ? 'Benchmark' : 'Standard'}
                  </td>
                  <td style={{ ...styles.td, color: '#a0a0b0', fontSize: '12px' }}>
                    {s.benchmark_label || 'Provisional'}
                  </td>
                  <td style={styles.td}>{s.rounds || '—'}</td>
                  <td style={styles.td}>{s.rph ? `${s.rph} @ ${s.spr}` : '—'}</td>
                  <td style={styles.td}>{s.hph || '—'}</td>
                  <td style={styles.td}>{s.TCO || '—'}</td>
                  <td style={styles.td}>{s.TTD || '—'}</td>
                  <td style={styles.td}>{s.TPU || '—'}</td>
                  <td style={styles.td}>{s.DPT || '—'}</td>
                  <td style={styles.td}>{s.TRT || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <Sidebar active="Reports" />
      <div style={styles.main}>
        <div style={styles.pageTitle}>Reports</div>
        <div style={styles.pageSubtitle}>View session reports and dealer performance trends</div>

        {view === 'session-detail' && renderSessionDetail()}
        {view === 'dealer-history' && renderDealerHistory()}

        {view === 'sessions' && (
          <>
            <div style={styles.tabRow}>
              <button style={styles.tabActive}>Session Reports</button>
              <button style={styles.tab} onClick={() => setView('dealer-history-search')}>
                Dealer History
              </button>
              <button style={styles.tab} onClick={() => setView('batch-reports')}>
                Batch Reports
              </button>
              <button style={styles.tab} onClick={() => setView('employee-compare')}>
                Employee Compare
              </button>
            </div>
            <div style={styles.card}>
                <div style={{ marginBottom: '20px' }}>
                <div style={styles.cardTitle}>Recent Sessions</div>
                <div style={{ display: 'flex', gap: '12px', marginTop: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <input
                    style={{ ...styles.input, width: '220px' }}
                    placeholder="Search name or payroll #"
                    value={searchFilter}
                    onChange={e => setSearchFilter(e.target.value)}
                  />
                  <select
                    style={{ ...styles.select, minWidth: '140px' }}
                    value={shiftFilter}
                    onChange={e => setShiftFilter(e.target.value)}
                  >
                    <option value="">All Shifts</option>
                    <option value="Day">Day</option>
                    <option value="Swing">Swing</option>
                    <option value="Grave">Grave</option>
                  </select>
                  <select
                    style={{ ...styles.select, minWidth: '160px' }}
                    value={purposeFilter}
                    onChange={e => setPurposeFilter(e.target.value)}
                  >
                    <option value="">All Purposes</option>
                    <option value="benchmark">Benchmark</option>
                    <option value="standard">Standard</option>
                  </select>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#a0a0b0', fontSize: '13px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={showExcluded}
                      onChange={e => setShowExcluded(e.target.checked)}
                    />
                    Show excluded sessions
                  </label>
                </div>
                {gameTypes.length > 1 && (
                  <div style={{ display: 'flex', gap: '10px', marginTop: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <span style={{ color: '#a0a0b0', fontSize: '12px', letterSpacing: '1px', textTransform: 'uppercase' }}>Games:</span>
                    <button
                      style={allGamesSelected ? styles.gameAllOn : styles.gameAll}
                      onClick={() => setSelectedGameIds(gameTypes.map(g => g.id))}
                      title="Show every game"
                    >
                      All Games
                    </button>
                    {families.map(family => {
                      const sel = selectedGameIds || [];
                      // A lone game stands on its own; there is no set to select.
                      if (family.games.length < 2) {
                        const only = family.games[0];
                        return (
                          <div key={family.id} style={styles.gameGroup}>
                            <button
                              style={sel.includes(only.id) ? styles.gameAllOn : styles.gameAll}
                              onClick={() => toggleGameFilter(only.id)}
                            >
                              {only.name}
                            </button>
                          </div>
                        );
                      }
                      const allIn = family.games.every(g => sel.includes(g.id));
                      return (
                        <div key={family.id} style={styles.gameGroup}>
                          <button
                            style={allIn ? styles.gameAllOn : styles.gameAll}
                            onClick={() => toggleFamilyFilter(family)}
                            title={'Show every ' + family.name + ' test'}
                          >
                            {family.name}
                          </button>
                          {family.games.map(g => (
                            <button
                              key={g.id}
                              style={sel.includes(g.id) ? styles.gameChipOn : styles.gameChip}
                              onClick={() => toggleGameFilter(g.id)}
                              title={g.name}
                            >
                              {variantLabel(family, g)}
                            </button>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                )}
                </div>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Dealer</th>
                    <th style={styles.th}>Date</th>
                    <th style={styles.th}>Game</th>
                    <th style={styles.th}>Pit</th>
                    <th style={styles.th}>Shift</th>
                    <th style={styles.th}>Type</th>
                    <th style={styles.th}>Rounds</th>
                    <th style={styles.th}>RPH @ SPR</th>
                    <th style={styles.th}>HPH</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions
                    .filter(s => showExcluded || !s.excluded)
                    .filter(s => !purposeFilter || (s.data_purpose || 'standard') === purposeFilter)
                    .map(session => (
                    <tr key={session.id} style={{ opacity: session.excluded ? 0.5 : 1 }}>
                      <td style={styles.td}>
                        {session.dealer_name}
                        {session.excluded && (
                          <span style={{ color: '#FFD700', fontSize: '11px', marginLeft: '6px' }}>
                            [excluded]
                          </span>
                        )}
                      </td>
                      <td style={styles.td}>{new Date(session.started_at).toLocaleDateString()}</td>
                      <td style={styles.td}>{session.game_name}</td>
                      <td style={styles.td}>{session.pit || '—'}</td>
                      <td style={styles.td}>{session.shift || '—'}</td>
                      {/* Purpose and type share a column. Every test is "standard"
                          unless it counts toward a benchmark, so only the exception
                          is worth a badge - printing "Standard" on every row was
                          noise that pushed the numbers off the screen. */}
                      <td style={{ ...styles.td, fontSize: '12px' }}>
                        {session.data_purpose === 'benchmark' && (
                          <div style={styles.purposeBadge}>BENCHMARK</div>
                        )}
                        {session.test_type && (
                          <div style={{
                            color: '#a0a0b0',
                            marginTop: session.data_purpose === 'benchmark' ? '4px' : 0,
                          }}>
                            {session.test_type}
                          </div>
                        )}
                        {!session.test_type && session.data_purpose !== 'benchmark' && (
                          <span style={{ color: '#6a7a95' }}>—</span>
                        )}
                      </td>
                      <td style={styles.td}>{session.round_count}</td>
                      <td style={styles.td}>
                        {session.rph ? `${session.rph} @ ${session.spr}` : '—'}
                      </td>
                      <td style={styles.td}>{session.hph || '—'}</td>
                      <td style={styles.actionCell}>
                        <div style={styles.actionRow}>
                          <button
                            style={{ ...styles.rowBtn, ...styles.rowBtnPrimary }}
                            onClick={() => viewSession(session)}
                          >
                            View
                          </button>
                          {isAdmin && (
                            <>
                              <button
                                style={{ ...styles.rowBtn, ...styles.rowBtnWarn }}
                                onClick={() => excludeSession(session.id, !session.excluded)}
                                title={session.excluded
                                  ? 'Put this test back into the figures'
                                  : 'Leave this test out of the figures, reversibly'}
                              >
                                {session.excluded ? 'Restore' : 'Exclude'}
                              </button>
                              <button
                                style={{ ...styles.rowBtn, ...styles.rowBtnGhost }}
                                onClick={() => changePurpose(session)}
                                title={session.data_purpose === 'benchmark'
                                  ? 'Stop this test counting toward the benchmark'
                                  : 'Count this test toward the benchmark'}
                              >
                                {session.data_purpose === 'benchmark' ? '→ Standard' : '→ Benchmark'}
                              </button>
                              <button
                                style={{ ...styles.rowBtn, ...styles.rowBtnDanger }}
                                onClick={() => deleteSession(session.id, session.dealer_name)}
                                title="Delete this test permanently"
                              >
                                Delete
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {sessions.length === 0 && (
                    <tr>
                      <td style={{ ...styles.td, color: '#a0a0b0' }} colSpan="10">
                        No sessions found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {view === 'dealer-history-search' && (
          <>
            <div style={styles.tabRow}>
              <button style={styles.tab} onClick={() => setView('sessions')}>Session Reports</button>
              <button style={styles.tabActive}>Dealer History</button>
              <button style={styles.tab} onClick={() => setView('batch-reports')}>
                Batch Reports
              </button>
              <button style={styles.tab} onClick={() => setView('employee-compare')}>
                Employee Compare
              </button>
            </div>
            <div style={styles.card}>
              <div style={styles.cardTitle}>Dealer Performance History</div>
              <div style={styles.filterRow}>
                <div style={styles.filterGroup}>
                  <label style={styles.label}>Search Dealer</label>
                  <input
                    style={styles.input}
                    placeholder="Payroll # or name"
                    value={dealerSearch}
                    onChange={e => {
                      setDealerSearch(e.target.value);
                      setSelectedDealer('');
                    }}
                  />
                  {dealerSearch && filteredDealers.length > 0 && (
                    <div style={styles.dropdownList}>
                      {filteredDealers.map(d => (
                        <div
                          key={d.id}
                          style={selectedDealer === d.id ? styles.dropdownItemSelected : styles.dropdownItem}
                          onClick={() => {
                            setSelectedDealer(d.id);
                            setDealerSearch(`${d.payroll_number} — ${d.first_name} ${d.last_name}`);
                          }}
                        >
                          {d.payroll_number} — {d.first_name} {d.last_name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div style={styles.filterGroup}>
                  <label style={styles.label}>From</label>
                  <input
                    style={styles.input}
                    type="date"
                    value={dateFrom}
                    onChange={e => setDateFrom(e.target.value)}
                  />
                </div>
                <div style={styles.filterGroup}>
                  <label style={styles.label}>To</label>
                  <input
                    style={styles.input}
                    type="date"
                    value={dateTo}
                    onChange={e => setDateTo(e.target.value)}
                  />
                </div>
                <button
                  style={styles.searchButton}
                  onClick={viewDealerHistory}
                  disabled={!selectedDealer || loading}
                >
                  {loading ? 'Loading...' : 'View History'}
                </button>
              </div>
            </div>
          </>
        )}

        {view === 'batch-reports' && (
          <>
            <div style={styles.tabRow}>
              <button style={styles.tab} onClick={() => setView('sessions')}>Session Reports</button>
              <button style={styles.tab} onClick={() => setView('dealer-history-search')}>Dealer History</button>
              <button style={styles.tabActive}>Batch Reports</button>
              <button style={styles.tab} onClick={() => setView('employee-compare')}>
                Employee Compare
              </button>
            </div>
            <BatchReportsTab />
          </>
        )}

        {view === 'employee-compare' && (
          <>
            <div style={styles.tabRow}>
              <button style={styles.tab} onClick={() => setView('sessions')}>Session Reports</button>
              <button style={styles.tab} onClick={() => setView('dealer-history-search')}>Dealer History</button>
              <button style={styles.tab} onClick={() => setView('batch-reports')}>Batch Reports</button>
              <button style={styles.tabActive}>Employee Compare</button>
            </div>
            <EmployeeCompare />
          </>
        )}
      </div>
    </div>
  );
}

export default Reports;